'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { navTourStepIndexForPathname } from '@communio/nav-tour-route-sync';
import type { NavTourStep } from './navTourSteps';
import { hrefForNavTourStep } from './navTourHref';

type Args = {
  step: NavTourStep | null;
  active: boolean;
  stepIds: readonly string[];
  setStepIndex: (index: number) => void;
  onEscape: () => void;
};

function pathsMatch(current: string, target: string): boolean {
  const a = current.replace(/\/$/, '') || '/';
  const b = target.replace(/\/$/, '') || '/';
  if (b === '/') return a === '/';
  return a === b || a.startsWith(`${b}/`);
}

/**
 * Sidebar path changes adopt the matching tour step.
 * Only force-route for marketplace-enable — pushing every social step remounts
 * SiteShell and raced bootstrap (URL still on Calendar → snap back).
 */
export function useNavTourChromeEffects({
  step,
  active,
  stepIds,
  setStepIndex,
  onEscape,
}: Args): void {
  const pathname = usePathname() || '/';
  const router = useRouter();
  const lastPushedStep = useRef<string | null>(null);
  const prevPathname = useRef(pathname);

  useEffect(() => {
    if (!active || stepIds.length === 0) return;
    if (prevPathname.current === pathname) return;
    prevPathname.current = pathname;
    const idx = navTourStepIndexForPathname(pathname, stepIds);
    if (idx === null) return;
    setStepIndex(idx);
  }, [active, pathname, stepIds, setStepIndex]);

  useEffect(() => {
    if (!step || !active) return;
    if (step.id !== 'marketplace-enable') return;
    const href = hrefForNavTourStep(step.id);
    if (!href) return;
    if (pathsMatch(pathname, href)) {
      lastPushedStep.current = step.id;
      return;
    }
    if (lastPushedStep.current === step.id) return;
    lastPushedStep.current = step.id;
    router.push(href);
  }, [step, active, pathname, router]);

  useEffect(() => {
    if (!active) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onEscape();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onEscape]);
}
